import { Injectable, Signal, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { RumBeforeSend, configureDatadog, getDatadogLogger } from "@storebrand-digital/common-datadog";
import { Environment } from "@storebrand-digital/common-datadog/dist/environment";
import { StbStage, getStbStage, isStageLocalhost } from "../utils/storebrand-staging";
import packageJSON from "../../../package.json";
import { applicationId, clientToken } from "../../environments/envconfig";
import { IGNORED_ERROR_SUBSTRINGS } from "../utils/monitoring";
import { KeycloakService } from "./keycloak.service";
import { getOrCreateSessionCorrelationId } from "../interceptors/correlation.interceptor";

/**
 * RUM-only noise: third-party errors that reach RUM as console or uncaught errors and that this app
 * can't fix. They never pass through `Monitoring`, so they are not in {@link IGNORED_ERROR_SUBSTRINGS}.
 */
const IGNORED_RUM_ERROR_SUBSTRINGS = [
  // console.error from the chatbot-web bubble when its 3rd-party cookie check iframe doesn't answer in time.
  "chatbot bubble initialization error",
  // console.error from unleash-proxy-client when a toggle poll can't reach the proxy (offline, blocked).
  // The client keeps the toggles it has. An error response from the proxy logs a different message, which is kept.
  "unleash: unable to fetch feature toggles",
];

/**
 * Browsers hide errors thrown by cross-origin scripts, so RUM only gets "Script error." with nothing to act on.
 * Exact match, so our own errors that start with the same words are kept.
 */
const CROSS_ORIGIN_SCRIPT_ERRORS = new Set(["script error.", 'uncaught "script error."']);

/**
 * RUM `beforeSend` noise filter. Drops the same known-noise events that the Datadog log
 * forwarder suppresses (see {@link IGNORED_ERROR_SUBSTRINGS}), plus the RUM-only third-party
 * errors above. The library's built-in PII scrub only masks 4+-digit runs (NIN/SSN/birthdate),
 * so these substrings survive unmasked and can be matched raw here. Returning `false` drops the event.
 */
const dropRumNoise: RumBeforeSend = (event): boolean => {
  const googleTranslate = IGNORED_ERROR_SUBSTRINGS.googleTranslate.toLowerCase();
  const keycloakCookieCheck = IGNORED_ERROR_SUBSTRINGS.keycloakCookieCheck.toLowerCase();

  if (event.type === "error") {
    const message = event.error.message?.toLowerCase() ?? "";
    if (message.includes(googleTranslate) || message.includes(keycloakCookieCheck)) {
      return false;
    }
    if (
      CROSS_ORIGIN_SCRIPT_ERRORS.has(message.trim()) ||
      IGNORED_RUM_ERROR_SUBSTRINGS.some((substring) => message.includes(substring))
    ) {
      return false;
    }
  }

  if (event.type === "resource") {
    const url = event.resource.url?.toLowerCase() ?? "";
    if (url.includes(keycloakCookieCheck)) {
      return false;
    }
  }

  return true;
};

const TOKEN_REFRESH_CONSOLE_ERROR = "couldn't update token";

/**
 * `@storebrand-digital/auth` logs `console.error("Couldn't update token", e)` when a background token
 * refresh fails, and RUM collects it. Once the session has ended (keycloak-js cleared the token) this
 * is expected, not a bug: the next API call sends the customer to login, and KeycloakService logs it
 * as info. While the customer is still logged in the failure is real, so the event is kept.
 */
const isTokenRefreshErrorAfterSessionEnd = (
  event: Parameters<RumBeforeSend>[0],
  authenticated: boolean | undefined,
): boolean =>
  event.type === "error" &&
  !authenticated &&
  (event.error.message?.toLowerCase() ?? "").startsWith(TOKEN_REFRESH_CONSOLE_ERROR);

@Injectable({
  providedIn: "root",
})
export class DatadogService {
  private readonly keycloakService = inject(KeycloakService);

  // cmid.user$ is backed by a (seeded) BehaviorSubject and replays synchronously, so the
  // signal holds the current value at construction. Created here (an injection context)
  // rather than read on demand, mirroring KeycloakService's own toSignal usage.
  private readonly cmid: Signal<string | undefined>;

  // Auth attribution that keycloak.initializer used to attach via Monitoring.setUser. Read as
  // signals here (an injection context), the same way cmid is, and attached as log context below.
  private readonly authMethod: Signal<string | undefined>;
  private readonly advisor: Signal<string | undefined>;

  public constructor() {
    this.cmid = toSignal(this.keycloakService.cmid.user$);
    this.authMethod = toSignal(this.keycloakService.amrFromToken$);
    this.advisor = toSignal(this.keycloakService.advisorSignature$);
  }

  public init(): void {
    if (isStageLocalhost()) {
      return;
    }

    const cmid = this.cmid();

    configureDatadog({
      clientPublicToken: clientToken,
      environment: getDatadogEnvironment(),
      // Must equal the source-map upload's --release-version (package.json
      // version) so Datadog can symbolicate minified stack traces. Using
      // environment.appVersion (a short git SHA) never matches that, which is
      // why RUM showed "no sourcemaps could be found".
      appVersion: packageJSON.version,
      appId: applicationId,
      appName: packageJSON.name,
      configureLogging: true,
      forwardConsoleLogs: ["error", "warn"],
      trackViewsManually: false,
      // Attribute RUM sessions/errors to the logged-in customer. common-datadog only
      // sets the RUM user at init time; this runs after the Keycloak app-initializer,
      // so the cmid is already resolved. Mirrors ../savings-web's initDatadog(cmid).
      user: cmid ? { cmid } : undefined,
      // Drop known-noise RUM events (Google Translate, Keycloak step2.html cookie check, chatbot, Unleash,
      // "Script error."), and the token refresh console error once the session has ended (read live, at
      // event time).
      beforeSend: (event, context) =>
        dropRumNoise(event, context) &&
        !isTokenRefreshErrorAfterSessionEnd(event, this.keycloakService.authenticated()),
    });

    this.enrichLogContext(cmid);
  }

  /**
   * Attach identifiers to every forwarded Datadog *log* so they are filterable and can be
   * cross-referenced with backend logs and the `x-correlation-id` HTTP header. The RUM user
   * (cmid) is set via `configureDatadog` above; the shared library exposes only the logger for
   * post-init enrichment, and direct `@datadog/*` imports are not permitted.
   *
   * `x-request-id` is intentionally omitted here: it is unique per request, so a single global
   * value would be stale. It is already attached per-error via the correlation/bearer interceptors.
   */
  private enrichLogContext(cmid: string | undefined): void {
    const logger = getDatadogLogger();
    logger.setContextProperty("correlationId", getOrCreateSessionCorrelationId());
    if (cmid) {
      logger.setContextProperty("cmid", cmid);
    }

    // Auth attribution previously carried by the now-removed Monitoring.setUser.
    const authMethod = this.authMethod();
    if (authMethod) {
      logger.setContextProperty("authMethod", authMethod);
    }

    const advisor = this.advisor();
    if (advisor) {
      logger.setContextProperty("advisor", advisor);
    }
  }
}

function getDatadogEnvironment(): Environment {
  const stage = getStbStage();
  switch (stage) {
    case StbStage.Localhost:
      return "development";
    case StbStage.Test:
    case StbStage.TestStabil:
      return "client-t";
    case StbStage.Produksjon:
    default:
      return "production";
  }
}
